import type JSZipType from "jszip";
import * as XLSX from "xlsx";
import type { AppRules, ReportResult } from "./types";

export function exportReport(result: ReportResult, rules: AppRules): void {
  const workbook = XLSX.utils.book_new();
  appendSheet(workbook, "周数据源", result.sourceRows, true);
  appendSheet(workbook, "透视模板数据源", toPivotTemplateRows(result.sourceRows), true);
  appendSheet(workbook, "周数据透视", result.weeklyRows, true);
  appendSheet(workbook, "月数据透视", result.monthlyRows, true);
  appendSheet(workbook, "异常清单", result.issues, true);
  appendSheet(workbook, "费用来源检查", result.feeChecks, true);
  appendSheet(workbook, "规则配置", rulesToRows(rules, result));
  XLSX.writeFile(workbook, `周数据_${timestamp()}.xlsx`, { compression: true });
}

export async function exportPivotReport(result: ReportResult): Promise<void> {
  const { default: JSZip } = await import("jszip");
  const response = await fetch("pivot-template.xlsx");
  if (!response.ok) {
    throw new Error(`透视模板加载失败：${response.status}`);
  }

  const zip = await JSZip.loadAsync(await response.arrayBuffer());
  const rows = toPivotTemplateRows(result.sourceRows);
  const rowCount = Math.max(rows.length, 1);
  const lastRow = rowCount + 1;
  const range = `A1:Y${lastRow}`;
  const absoluteRange = `$A$1:$Y$${lastRow}`;

  await replaceZipText(zip, "xl/worksheets/sheet3.xml", (xml) => {
    const sheetData = buildSheetData(rows);
    return xml
      .replace(/<dimension ref="[^"]*"/, `<dimension ref="${range}"`)
      .replace(/<sheetData>[\s\S]*?<\/sheetData>/, sheetData);
  });

  await replaceZipText(zip, "xl/tables/table1.xml", (xml) =>
    xml
      .replace(/ref="A1:Y\d+"/g, `ref="${range}"`)
      .replace(/<autoFilter ref="A1:Y\d+"([^>]*)\/>/, `<autoFilter ref="${range}"$1/>`)
  );

  await replaceZipText(zip, "xl/workbook.xml", (xml) =>
    xml.replace(
      /<definedName name="ExternalData_2"[^>]*>[\s\S]*?<\/definedName>/,
      `<definedName name="ExternalData_2" localSheetId="2" hidden="1">周数据源!${absoluteRange}</definedName>`
    )
  );

  await replaceZipText(zip, "xl/pivotCache/pivotCacheDefinition1.xml", (xml) =>
    withXmlAttribute(xml, "pivotCacheDefinition", {
      refreshOnLoad: "1",
      enableRefresh: "1"
    })
  );

  await replaceZipText(zip, "xl/pivotTables/pivotTable1.xml", (xml) =>
    withXmlAttribute(xml, "pivotTableDefinition", { enableDrill: "1" })
  );
  await replaceZipText(zip, "xl/pivotTables/pivotTable2.xml", (xml) =>
    withXmlAttribute(xml, "pivotTableDefinition", { enableDrill: "1" })
  );

  const blob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    compression: "DEFLATE"
  });
  downloadBlob(blob, `周数据_带透视表_${timestamp()}.xlsx`);
}

export const pivotTemplateColumns = [
  "channle",
  "周区间",
  "日期",
  "listing name",
  "asin",
  "sku",
  "总实际销售额",
  "总销量",
  "总配送费",
  "曝光",
  "点击",
  "花费",
  "总广告单量",
  "总广告销量",
  "广告sku销量",
  "广告其他sku销量",
  "广告sku销售额",
  "总广告销售额",
  "广告其他sku销售额",
  "仓储费/天",
  "调整金额",
  "退款数量",
  "退款金额",
  "COSTOMER RETURN.总落地成本",
  "总落地成本"
] as const;

export function toPivotTemplateRows(rows: ReportResult["sourceRows"]): Array<Record<string, unknown>> {
  return rows.map((row) => {
    const output: Record<string, unknown> = {};
    for (const column of pivotTemplateColumns) output[column] = row[column];
    return output;
  });
}

function buildSheetData(rows: Array<Record<string, unknown>>): string {
  const dataRows = rows.length ? rows : [blankPivotTemplateRow()];
  const allRows = [pivotTemplateColumns, ...dataRows.map((row) => pivotTemplateColumns.map((column) => row[column]))];
  return `<sheetData>${allRows.map((row, index) => buildSheetRow(row, index + 1)).join("")}</sheetData>`;
}

function blankPivotTemplateRow(): Record<string, unknown> {
  return Object.fromEntries(pivotTemplateColumns.map((column) => [column, ""]));
}

function buildSheetRow(values: readonly unknown[], rowNumber: number): string {
  const cells = values
    .map((value, index) => buildCell(colName(index + 1), rowNumber, value, index))
    .filter(Boolean)
    .join("");
  return `<row r="${rowNumber}" spans="1:25">${cells}</row>`;
}

function buildCell(columnName: string, rowNumber: number, value: unknown, columnIndex: number): string {
  const ref = `${columnName}${rowNumber}`;
  if (value == null || value === "") return `<c r="${ref}"/>`;
  if (rowNumber > 1 && columnIndex === 2) {
    const serial = excelDateSerial(String(value));
    return Number.isFinite(serial) ? `<c r="${ref}" s="4"><v>${serial}</v></c>` : inlineStringCell(ref, value);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return `<c r="${ref}"><v>${normalizeNumber(value)}</v></c>`;
  }
  return inlineStringCell(ref, value);
}

function inlineStringCell(ref: string, value: unknown): string {
  return `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(String(value))}</t></is></c>`;
}

function excelDateSerial(value: string): number {
  const [year, month, day] = value.split("-").map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) return Number.NaN;
  return Date.UTC(year, month - 1, day) / 86400000 + 25569;
}

function normalizeNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(12)));
}

async function replaceZipText(zip: JSZipType, path: string, replacer: (text: string) => string): Promise<void> {
  const file = zip.file(path);
  if (!file) throw new Error(`模板缺少文件：${path}`);
  zip.file(path, replacer(await file.async("text")));
}

function withXmlAttribute(xml: string, tagName: string, attributes: Record<string, string>): string {
  return xml.replace(new RegExp(`<${tagName}\\b([^>]*)>`), (match, rawAttrs: string) => {
    let attrs = rawAttrs as string;
    for (const [name, value] of Object.entries(attributes)) {
      const pattern = new RegExp(`\\s${name}="[^"]*"`);
      attrs = pattern.test(attrs) ? attrs.replace(pattern, ` ${name}="${value}"`) : `${attrs} ${name}="${value}"`;
    }
    return `<${tagName}${attrs}>`;
  });
}

function colName(number: number): string {
  let name = "";
  while (number > 0) {
    number -= 1;
    name = String.fromCharCode(65 + (number % 26)) + name;
    number = Math.floor(number / 26);
  }
  return name;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function appendSheet<T extends object>(workbook: XLSX.WorkBook, name: string, rows: T[], addFilter = false): void {
  const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ message: "无数据" }]);
  const range = XLSX.utils.decode_range(sheet["!ref"] ?? "A1:A1");
  const widths: XLSX.ColInfo[] = [];
  for (let col = range.s.c; col <= range.e.c; col += 1) {
    let max = 10;
    for (let row = range.s.r; row <= Math.min(range.e.r, range.s.r + 200); row += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
      const text = cell?.v == null ? "" : String(cell.v);
      max = Math.max(max, Math.min(32, text.length + 2));
    }
    widths.push({ wch: max });
  }
  sheet["!cols"] = widths;
  if (addFilter) {
    sheet["!autofilter"] = { ref: XLSX.utils.encode_range(range) };
    sheet["!freeze"] = { xSplit: 0, ySplit: 1 };
  }
  XLSX.utils.book_append_sheet(workbook, sheet, name);
}

function rulesToRows(rules: AppRules, result: ReportResult): Array<Record<string, string | number>> {
  return [
    { 项目: "佣金率", 值: rules.commissionRate },
    { 项目: "旺季月份", 值: rules.peakMonths.join(",") },
    { 项目: "旺季倍数", 值: rules.peakMultiplier },
    { 项目: "SB SKU 分隔符", 值: rules.sbSkuDelimiter },
    { 项目: "订单时区", 值: rules.timezone },
    { 项目: "产品数", 值: result.stats.productCount },
    { 项目: "周数据源行数", 值: result.stats.sourceRowCount },
    { 项目: "订单行数", 值: result.stats.orderCount },
    { 项目: "Transaction文件数", 值: result.stats.transactionFileCount },
    { 项目: "Transaction行数", 值: result.stats.transactionRowCount },
    { 项目: "开始日期", 值: result.stats.earliestDate },
    { 项目: "结束日期", 值: result.stats.latestDate }
  ];
}

function timestamp(): string {
  const date = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}`;
}
